import { Text, Button, Collapse } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconChevronDown, IconChevronRight, IconChevronsDown, IconChevronsUp } from '@tabler/icons-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAtom } from 'jotai';
import { cn } from '~/common/string/classname';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { adminUiHiddenAtom } from '~/atoms/atoms';

// Height constants for easy adjustment
const DEFAULT_MAX_HEIGHT = 150;
const EXPANDED_MAX_HEIGHT = 1000;

export function CollapsibleCard({
  icon: Icon,
  title,
  badge,
  headerButtons = [],
  classNames,
  children,
  defaultExpanded = false,
}: {
  icon: React.ComponentType<{ size?: string | number; className?: string }>;
  title: string;
  badge?: string;
  headerButtons?: {
    name: string;
    icon: React.ComponentType<{ size?: string | number; className?: string }>;
    callback: () => void;
    headerButtonCompletedIcon?: React.ComponentType<{ size?: string | number; className?: string }>;
    adminOnly?: boolean;
  }[];
  classNames?: {
    container?: string;
    header?: string;
    icon?: string;
  };
  children?: ReactNode;
  defaultExpanded?: boolean;
}) {
  const { isAdmin } = useAuthData();
  const [adminUiHidden] = useAtom(adminUiHiddenAtom);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isHeightExpanded, setIsHeightExpanded] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [showExpandArrow, setShowExpandArrow] = useState(false);
  const [headerButtonsCompletedStates, setHeaderButtonsCompletedStates] = useState<Record<number, boolean>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRefs = useRef<Record<number, NodeJS.Timeout>>({});

  // Calculate and update UI state based on scroll position and content dimensions
  const updateUIStateBasedOnScrollAndOverflowState = useCallback(() => {
    if (contentRef.current) {
      const {
        scrollHeight: totalContentHeight,
        clientHeight: visibleHeight,
        scrollTop: scrolledDistance,
      } = contentRef.current;

      const isContentOverflowing = totalContentHeight > visibleHeight;

      // Check if user has scrolled to the bottom (with 1px tolerance for sub-pixel rendering)
      const isUserAtBottom = scrolledDistance + visibleHeight >= totalContentHeight - 1;

      // Show bottom shadow only when content overflows and user hasn't reached bottom
      setShowBottomShadow(isContentOverflowing && !isUserAtBottom);

      // Determine if content would overflow the current height limit
      const currentMaxHeight = isHeightExpanded ? EXPANDED_MAX_HEIGHT : DEFAULT_MAX_HEIGHT;
      const contentExceedsHeightLimit = totalContentHeight > currentMaxHeight;

      // Show expand arrow only when content overflows, not already height-expanded, and user hasn't reached bottom
      setShowExpandArrow(contentExceedsHeightLimit && !isHeightExpanded && !isUserAtBottom);
    }
  }, [isHeightExpanded]);

  // Debounced version to handle rapid resize events
  const debouncedUpdate = useDebouncedCallback(updateUIStateBasedOnScrollAndOverflowState, 100);

  useEffect(() => {
    // when card is expanded and content element exists, update UI state based on scroll and overflow state
    if (isExpanded && contentRef.current) {
      updateUIStateBasedOnScrollAndOverflowState();

      const element = contentRef.current;

      element.addEventListener('scroll', updateUIStateBasedOnScrollAndOverflowState);

      // Listen for content size changes (e.g., dynamic content loading)
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      resizeObserver.observe(element);

      // Also listen for window resize events to catch parent container width changes
      window.addEventListener('resize', debouncedUpdate);

      return () => {
        element.removeEventListener('scroll', updateUIStateBasedOnScrollAndOverflowState);
        resizeObserver.disconnect();
        window.removeEventListener('resize', debouncedUpdate);
      };
    }
  }, [isExpanded, children, updateUIStateBasedOnScrollAndOverflowState, debouncedUpdate]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout);
    };
  }, []);

  const handleButtonClick = useCallback((headerButton: (typeof headerButtons)[0], index: number) => {
    // Execute the callback
    headerButton.callback();

    // If there's a completed icon, show it for 1.5 seconds
    if (headerButton.headerButtonCompletedIcon) {
      // Clear any existing timeout for this button
      if (timeoutRefs.current[index]) {
        clearTimeout(timeoutRefs.current[index]);
      }

      // Set completed state
      setHeaderButtonsCompletedStates((prev) => ({ ...prev, [index]: true }));

      // Set timeout to revert after 1.5 seconds
      timeoutRefs.current[index] = setTimeout(() => {
        setHeaderButtonsCompletedStates((prev) => ({ ...prev, [index]: false }));
        delete timeoutRefs.current[index];
      }, 1500);
    }
  }, []);

  const maxHeight = isHeightExpanded ? EXPANDED_MAX_HEIGHT : DEFAULT_MAX_HEIGHT;

  return (
    <div className={cn('overflow-hidden rounded border border-gray-200 dark:border-gray-700', classNames?.container)}>
      {/* Header */}
      <div
        className={cn(
          'group flex items-center bg-gray-100 px-3 dark:bg-gray-800',
          children ? 'py-0' : 'py-2',
          classNames?.header,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center space-x-2">
          <div className="flex-shrink-0">
            <Icon
              size={14}
              className={cn('text-gray-600 dark:text-gray-400', classNames?.icon)}
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center space-x-2">
            <Text
              size="sm"
              className="truncate font-medium leading-tight text-gray-800 dark:text-gray-200"
              title={title}
            >
              {title}
            </Text>
            {!!badge && (
              <div className="flex-shrink-0">
                <Text
                  size="xs"
                  className="rounded border border-gray-300 bg-gray-100 px-2 font-medium uppercase tracking-wide text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                >
                  {badge}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Header Buttons */}
        {!!headerButtons.length && (
          <div className="ml-2 flex items-center space-x-1">
            {headerButtons
              .filter((headerButton) => !headerButton.adminOnly || (isAdmin && !adminUiHidden))
              .map((headerButton, index) => {
                const isCompleted = headerButtonsCompletedStates[index];
                const IconComponent =
                  isCompleted && headerButton.headerButtonCompletedIcon ?
                    headerButton.headerButtonCompletedIcon
                  : headerButton.icon;

                return (
                  <Button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonClick(headerButton, index);
                    }}
                    className={cn(
                      'rounded bg-transparent px-1 transition-colors duration-200',
                      headerButton.adminOnly ?
                        'text-orange-500 hover:bg-orange-50 hover:text-orange-600 dark:text-orange-400 dark:hover:bg-orange-900/20 dark:hover:text-orange-300'
                      : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200',
                      {
                        'opacity-0 transition-opacity duration-200 group-hover:opacity-100':
                          headerButton.name === 'Copy Content',
                        'text-green-600 dark:text-green-400': isCompleted,
                      },
                    )}
                    title={headerButton.name}
                    variant="unstyled"
                  >
                    <IconComponent size={14} />
                  </Button>
                );
              })}
          </div>
        )}

        {!!children && (
          <div className="ml-2 flex-shrink-0">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              className="rounded bg-gray-100 px-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:bg-gray-800 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              title={isExpanded ? 'Collapse' : 'Expand'}
              variant="unstyled"
            >
              {isExpanded ?
                <IconChevronDown size={12} />
              : <IconChevronRight size={12} />}
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      <Collapse in={!!isExpanded && !!children}>
        <div className="relative">
          <div
            ref={contentRef}
            className={cn(
              'overflow-y-auto border-t border-gray-200 bg-gray-50/50 px-3 py-2 text-black dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-200',
              isHeightExpanded ? 'pb-8' : 'pb-2',
            )}
            style={{ maxHeight: `${maxHeight}px` }}
          >
            {children}
          </div>

          {/* Bottom shadow gradient - stronger when arrow is present */}
          {!!showBottomShadow && (
            <div
              className={cn(
                'pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t',
                showExpandArrow || isHeightExpanded ?
                  'from-gray-200 via-gray-100/90 to-transparent dark:from-gray-900 dark:via-gray-800/90 dark:to-transparent'
                : 'from-gray-100 via-gray-50/80 to-transparent dark:from-gray-900 dark:via-gray-800/80 dark:to-transparent',
              )}
            />
          )}

          {/* Expand/Collapse arrow - centered at bottom */}
          {!!(showExpandArrow || isHeightExpanded) && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 pb-0">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHeightExpanded(!isHeightExpanded);
                }}
                className="bg-transparent text-gray-600 hover:bg-transparent hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                title={isHeightExpanded ? 'Collapse' : 'Expand'}
                variant="unstyled"
              >
                {isHeightExpanded ?
                  <IconChevronsUp size={16} />
                : <IconChevronsDown size={16} />}
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}
